import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Crown, CheckCircle, ArrowRight, Sparkle, Tag } from "@phosphor-icons/react";

const FREE_FEATURES = [
  "5 documents",
  "10 Vault Coach sessions / month",
  "All vault sections + tracker",
  "JSON export + browser extension",
];
const PREMIUM_FEATURES = [
  "25 documents",
  "100 Vault Coach sessions / month",
  "Public share link (/v/yourname)",
  "Cancel anytime",
];
const UNLIMITED_FEATURES = [
  "Unlimited documents",
  "Unlimited Vault Coach sessions",
  "Public share link",
  "Early access to new features",
];

const TIER_ORDER = { free: 0, premium: 1, unlimited: 2 };

export default function Billing() {
  const [usage, setUsage] = useState(null);
  const [pkgCatalog, setPkgCatalog] = useState(null);
  const [interval, setInterval] = useState("annual");  // default to annual so the savings anchor
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const [{ data: me }, { data: pkgs }] = await Promise.all([
      api.get("/billing/me"),
      api.get("/billing/packages"),
    ]);
    setUsage(me);
    setPkgCatalog(pkgs);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      toast.info("Checkout canceled");
      params.delete("canceled"); setParams(params);
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (data.status === "completed" ||
            data.payment_status === "paid" ||
            data.payment_status === "no_payment_required") {
          toast.success(data.tier_granted === "unlimited" ? "Welcome to Unlimited 🎉" : "Welcome to Premium 🎉");
          params.delete("session_id"); setParams(params);
          load();
          return;
        }
        if (data.status === "expired") {
          toast.error("Session expired"); params.delete("session_id"); setParams(params); return;
        }
        if (attempts++ < 6) setTimeout(poll, 2500);
        else toast.info("Still processing — refresh in a minute");
      } catch {
        toast.error("Couldn't verify payment");
      }
    };
    poll();
  }, [params, setParams]);

  const goToStripe = (url) => {
    if (!url) return false;
    try {
      const a = document.createElement("a");
      a.href = url; a.target = "_top"; a.rel = "noopener noreferrer";
      document.body.appendChild(a); a.click(); a.remove();
      return true;
    } catch { /* fall through */ }
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (w) return true;
    toast.error(
      <span>
        Popup blocked.{" "}
        <a href={url} target="_top" rel="noopener noreferrer" className="underline font-medium">
          Click here to continue to Stripe
        </a>
      </span>,
      { duration: 20000 },
    );
    return false;
  };

  const startCheckout = async (packageId) => {
    setLoadingPkg(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (!data?.url) {
        toast.error("Checkout URL missing — try again");
        setLoadingPkg(null);
        return;
      }
      goToStripe(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout");
      setLoadingPkg(null);
    }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/billing` });
      if (data?.url) goToStripe(data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open portal");
    }
  };

  const currentTier = usage?.tier || "free";
  const currentRank = TIER_ORDER[currentTier] ?? 0;
  const billingEnabled = usage?.billing_enabled !== false;  // default true if missing
  const showManage = billingEnabled && usage?.live_mode && usage?.has_stripe_customer && currentTier !== "free";

  // Look up tier pricing from the API response (single source of truth).
  const premiumTier = pkgCatalog?.tiers?.find((t) => t.id === "premium");
  const unlimitedTier = pkgCatalog?.tiers?.find((t) => t.id === "unlimited");
  const isAnnual = interval === "annual";

  // The biggest savings % across tiers — used in the toggle badge for max impact.
  const headlineSavings = Math.max(
    premiumTier?.annual?.savings_percent || 0,
    unlimitedTier?.annual?.savings_percent || 0,
  );

  return (
    <AppShell title="Billing & Plan">
      <p className="text-[var(--brand-muted)] mb-6 max-w-2xl">
        free covers the basics. premium and unlimited remove limits + unlock the public share link.
      </p>

      {/* Monthly ↔ Annual toggle */}
      {pkgCatalog && (
        <div className="card-hard rounded-md bg-white p-1.5 mb-8 inline-flex items-center" data-testid="billing-interval-toggle">
          <button
            onClick={() => setInterval("monthly")}
            data-testid="interval-monthly"
            aria-pressed={!isAnnual}
            className={`px-4 py-2 rounded text-sm font-medium transition ${
              !isAnnual ? "bg-[var(--brand-ink)] text-white" : "hover:bg-[var(--brand-bg)]"
            }`}
          >
            monthly
          </button>
          <button
            onClick={() => setInterval("annual")}
            data-testid="interval-annual"
            aria-pressed={isAnnual}
            className={`px-4 py-2 rounded text-sm font-medium transition flex items-center gap-2 ${
              isAnnual ? "bg-[var(--brand-ink)] text-white" : "hover:bg-[var(--brand-bg)]"
            }`}
          >
            annual
            {headlineSavings > 0 && (
              <span
                data-testid="annual-savings-pill"
                className={`label-tiny px-2 py-0.5 rounded-full ${
                  isAnnual ? "bg-[var(--bolt-sage)] text-[var(--brand-ink)]" : "bg-[var(--pastel-mint)]"
                }`}
              >
                save {headlineSavings}%
              </span>
            )}
          </button>
        </div>
      )}

      {usage && (
        <div className="card-hard rounded-md p-6 bg-[var(--pastel-yellow)] mb-8" data-testid="usage-card">
          <p className="label-tiny mb-2">/your usage · {usage.ai_rewrites.month}</p>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="font-display text-3xl font-semibold tracking-tight" data-testid="usage-docs">
                {usage.documents.used}{usage.documents.limit ? ` / ${usage.documents.limit}` : ""}
              </p>
              <p className="text-xs label-tiny mt-1">documents</p>
            </div>
            <div>
              <p className="font-display text-3xl font-semibold tracking-tight" data-testid="usage-ai">
                {usage.ai_rewrites.used}{usage.ai_rewrites.limit ? ` / ${usage.ai_rewrites.limit}` : ""}
              </p>
              <p className="text-xs label-tiny mt-1">coach sessions</p>
            </div>
          </div>
          {showManage && (
            <button onClick={openPortal} data-testid="manage-subscription"
              className="mt-5 btn-hard bg-[var(--brand-ink)] text-white rounded-md px-4 py-2 text-sm hover:bg-[var(--brand-ink)]">
              manage subscription
            </button>
          )}
        </div>
      )}

      {!billingEnabled && (
        <div className="card-hard rounded-md p-4 mb-6 bg-[var(--pastel-yellow)] flex items-start gap-3" data-testid="billing-disabled-notice">
          <Sparkle size={18} weight="fill" className="mt-0.5 shrink-0"/>
          <p className="text-sm">
            <span className="font-medium">Billing is paused in this environment.</span> You can preview the plans, but checkout is disabled until Stripe is wired up. On the live site, these tiles take you straight to a Stripe checkout.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free */}
        <PlanCard
          testid="plan-free"
          name="free"
          headline="£0"
          subline="forever. no card. no nonsense."
          features={FREE_FEATURES}
          current={currentTier === "free"}
          locked={currentRank > TIER_ORDER.free}
          accent="bg-white"
        />

        {/* Premium */}
        {premiumTier && (
          <PlanCard
            testid="plan-premium"
            name="premium"
            headline={isAnnual
              ? `£${premiumTier.annual.monthly_equivalent.toFixed(2)}`
              : `£${premiumTier.monthly.amount.toFixed(2)}`}
            headlineSuffix="/month"
            footnote={isAnnual
              ? `£${premiumTier.annual.amount.toFixed(2)} billed yearly · save £${premiumTier.annual.savings.toFixed(2)} (${premiumTier.annual.savings_percent}%)`
              : "billed monthly · cancel anytime"}
            subline="for active applicants."
            features={PREMIUM_FEATURES}
            current={currentTier === "premium"}
            locked={currentRank > TIER_ORDER.premium}
            ctaLabel={billingEnabled && currentRank < TIER_ORDER.premium ? "upgrade to premium" : null}
            onCta={() => startCheckout(isAnnual ? "premium_annual" : "premium_monthly")}
            loading={loadingPkg === (isAnnual ? "premium_annual" : "premium_monthly")}
            accent="bg-[var(--pastel-mint)]"
            icon={Sparkle}
            showSavingsRibbon={isAnnual && premiumTier.annual.savings_percent > 0 ? premiumTier.annual.savings_percent : 0}
          />
        )}

        {/* Unlimited */}
        {unlimitedTier && (
          <PlanCard
            testid="plan-unlimited"
            name="unlimited"
            headline={isAnnual
              ? `£${unlimitedTier.annual.monthly_equivalent.toFixed(2)}`
              : `£${unlimitedTier.monthly.amount.toFixed(2)}`}
            headlineSuffix="/month"
            footnote={isAnnual
              ? `£${unlimitedTier.annual.amount.toFixed(2)} billed yearly · save £${unlimitedTier.annual.savings.toFixed(2)} (${unlimitedTier.annual.savings_percent}%)`
              : "billed monthly · cancel anytime"}
            subline="for the chronically applying."
            features={UNLIMITED_FEATURES}
            current={currentTier === "unlimited"}
            ctaLabel={billingEnabled && currentTier !== "unlimited" ? "upgrade to unlimited" : null}
            onCta={() => startCheckout(isAnnual ? "unlimited_annual" : "unlimited_monthly")}
            loading={loadingPkg === (isAnnual ? "unlimited_annual" : "unlimited_monthly")}
            accent="bg-[var(--pastel-peach)]"
            icon={Crown}
            highlight
            showSavingsRibbon={isAnnual && unlimitedTier.annual.savings_percent > 0 ? unlimitedTier.annual.savings_percent : 0}
          />
        )}
      </div>

      <p className="font-mono-d text-xs text-[var(--brand-muted)] mt-8">
        secure checkout via Stripe · {usage?.live_mode ? "live mode" : "test mode"}
      </p>
    </AppShell>
  );
}

function PlanCard({
  testid, name, headline, headlineSuffix, subline, footnote, features,
  current, locked, ctaLabel, onCta, loading, accent, icon: Icon, highlight, showSavingsRibbon,
}) {
  return (
    <div
      data-testid={testid}
      className={`relative card-hard rounded-md p-6 ${accent} ${current ? "ring-4 ring-[var(--brand-primary)]" : ""} ${highlight ? "md:scale-[1.02]" : ""}`}
    >
      {showSavingsRibbon > 0 && (
        <div
          data-testid={`${testid}-savings-ribbon`}
          className="absolute -top-3 right-4 card-hard rounded-full px-3 py-1 bg-[var(--bolt-sage)] flex items-center gap-1.5"
        >
          <Tag size={12} weight="fill"/>
          <span className="label-tiny" style={{ letterSpacing: "0.1em" }}>save {showSavingsRibbon}%</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="label-tiny flex items-center gap-1">
          {Icon && <Icon size={12} weight="fill"/>} /{name}
        </p>
        {current && <span className="label-tiny bg-[var(--brand-primary)] text-white px-2 py-0.5 rounded">current</span>}
      </div>

      <p className="font-display text-4xl font-semibold tracking-tight mt-2" data-testid={`${testid}-price`}>
        {headline}
        {headlineSuffix && headline !== "£0" && (
          <span className="text-base font-normal text-[var(--brand-muted)]">{headlineSuffix}</span>
        )}
      </p>
      {footnote && (
        <p className="font-mono-d text-[11px] text-[var(--brand-muted)] mt-1" data-testid={`${testid}-footnote`}>{footnote}</p>
      )}
      <p className="text-sm text-[var(--brand-muted)] mb-5 mt-2">{subline}</p>
      <ul className="space-y-2 mb-6">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm">
            <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0"/>{f}
          </li>
        ))}
      </ul>
      {current ? (
        name === "unlimited" ? (
          <p className="font-mono-d text-xs label-tiny text-[var(--brand-muted)]" data-testid={`${testid}-status`}>
            you&apos;re on unlimited — go off, mate.
          </p>
        ) : (
          <p className="font-mono-d text-xs label-tiny text-[var(--brand-muted)]" data-testid={`${testid}-status`}>current plan</p>
        )
      ) : locked ? (
        <p className="font-mono-d text-xs label-tiny text-[var(--brand-muted)]" data-testid={`${testid}-status`}>downgrade via &ldquo;manage subscription&rdquo;</p>
      ) : ctaLabel ? (
        <button onClick={onCta} disabled={loading} data-testid={`${testid}-cta`}
          className={`btn-hard text-white rounded-md px-5 py-3 w-full font-medium flex items-center justify-center gap-2 disabled:opacity-60 ${
            highlight ? "bg-[var(--brand-primary)] hover:bg-[var(--brand-primary)]" : "bg-[var(--brand-ink)] hover:bg-[var(--brand-ink)]"
          }`}>
          {loading ? "redirecting…" : <>{ctaLabel} <ArrowRight weight="bold" size={16}/></>}
        </button>
      ) : null}
    </div>
  );
}
